"use client";

import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  Sparkles,
  Loader2,
  RefreshCw,
  TrendingUp,
  AlertTriangle,
  Lightbulb,
  ChevronDown,
  ChevronUp,
  X,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface Insight {
  type: "trend" | "alert" | "tip";
  message: string;
}

function InsightIcon({ type }: { type: string }) {
  switch (type) {
    case "trend":
      return <TrendingUp className="h-4 w-4 text-emerald-500" />;
    case "alert":
      return <AlertTriangle className="h-4 w-4 text-amber-500" />;
    case "tip":
      return <Lightbulb className="h-4 w-4 text-blue-400" />;
    default:
      return <Sparkles className="h-4 w-4 text-indigo-400" />;
  }
}

export function DashboardInsights({ dashboardData }: { dashboardData?: any }) {
  const [dismissed, setDismissed] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const {
    mutate: generateInsights,
    data: insights,
    isPending,
    error: insightError,
  } = useMutation({
    mutationFn: async (): Promise<Insight[]> => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 45000);

      try {
        const res = await fetch("/api/ai/chat", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            messages: [
              {
                role: "user",
                content: `Analyse les données du dashboard et génère 3-4 insights courts et actionnables. Réponds UNIQUEMENT en JSON :
[{"type": "trend|alert|tip", "message": "..."}]

Données :
${JSON.stringify(dashboardData || {}, null, 2).slice(0, 2000)}

Si les données sont vides, génère des insights génériques utiles pour un gérant d'entreprise d'électricité.`,
              },
            ],
            pageContext: { page: "Dashboard", path: "/" },
            stream: false,
          }),
          signal: controller.signal,
        });

        clearTimeout(timeout);

        if (!res.ok) throw new Error("Erreur");
        const data = await res.json();

        try {
          const match = data.content?.match(/\[[\s\S]*\]/);
          if (match) return JSON.parse(match[0]);
        } catch {
          // fallback
        }

        return [
          { type: "tip", message: "Configurez le service IA pour des insights personnalisés basés sur vos données." },
        ];
      } catch (err: any) {
        clearTimeout(timeout);
        if (err.name === "AbortError") {
          return [
            { type: "alert", message: "L'IA n'a pas répondu dans le délai imparti (45s). Vérifiez Paramètres > IA." },
          ];
        }
        return [
          { type: "alert", message: "Service IA indisponible. Vérifiez la configuration dans Paramètres > IA." },
        ];
      }
    },
  });

  if (dismissed) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.6 }}
    >
      <Card className="border-indigo-500/20 bg-indigo-500/[0.02]">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-indigo-500" />
              Insights IA
            </CardTitle>
            <div className="flex items-center gap-1">
              {insights && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => generateInsights()}
                >
                  <RefreshCw className={cn("h-3 w-3", isPending && "animate-spin")} />
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => setExpanded(!expanded)}
              >
                {expanded ? (
                  <ChevronUp className="h-3 w-3" />
                ) : (
                  <ChevronDown className="h-3 w-3" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => setDismissed(true)}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </CardHeader>

        {expanded && (
          <CardContent className="pt-0">
            {!insights && !isPending && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => generateInsights()}
                className="w-full gap-2 text-xs"
              >
                <Sparkles className="h-3.5 w-3.5" />
                Générer les insights
              </Button>
            )}

            {isPending && (
              <div className="flex items-center justify-center py-4 gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Analyse en cours...
              </div>
            )}

            {insights && !isPending && (
              <div className="space-y-2">
                {insights.map((insight, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm">
                    <InsightIcon type={insight.type} />
                    <p className="text-muted-foreground leading-relaxed text-xs">
                      {insight.message}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        )}
      </Card>
    </motion.div>
  );
}
